import type { Scene } from '@babylonjs/core/scene';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { AssetLoader } from '../assets/AssetLoader';
import type { AssetRegistry } from '../assets/AssetRegistry';
import { GreyboxMaterialId, type AssetId, type GreyboxPart } from '../assets/AssetTypes';
import type { EntityVisual } from './EntityVisual';
import type { GreyboxFactory } from './greybox/GreyboxFactory';

/**
 * Pipeline helper nodes inside GLBs (sockets, connectors, lanes, ad surfaces,
 * colliders) are authoring/validation data – the game uses the validated
 * segment data instead, so they are not instantiated at runtime.
 */
const AUTHORING_NODE = /^(SOCKET_|CONNECTOR_|LANE_L|AD_|JUNCTION_PIVOT|COLLIDER_)/;

const MISSING_PARTS: readonly GreyboxPart[] = [
  { shape: 'box', size: [1, 1, 1], position: [0, 0.5, 0], material: GreyboxMaterialId.Missing },
];

/**
 * Resolves an asset id to a renderable visual – greybox or GLB, decided by
 * the manifest only. Unloaded/failed GLBs fall back to their greybox (or a
 * magenta box), so a missing file never breaks a run.
 */
export class VisualFactory {
  private counter = 0;
  private readonly warned = new Set<AssetId>();

  constructor(
    private readonly scene: Scene,
    private readonly registry: AssetRegistry,
    private readonly greybox: GreyboxFactory,
    private readonly loader: AssetLoader,
  ) {}

  /** Pre-builds greybox sources so the first spawn does not stall. */
  prepareGreyboxes(): void {
    for (const entry of this.registry.all()) {
      if (entry.source.kind === 'greybox') this.greybox.prepare(entry.id, entry.source.parts, entry.source.rig);
    }
  }

  /** A fallback whose real GLB has loaded in the meantime – must not be reused. */
  isStale(visual: EntityVisual): boolean {
    return visual.isFallback === true && this.loader.get(visual.assetId) !== undefined;
  }

  create(assetId: AssetId): EntityVisual {
    const entry = this.registry.get(assetId);
    const name = `${assetId}#${++this.counter}`;

    if (entry.source.kind === 'greybox') {
      const root = this.greybox.instantiate(assetId, entry.source.parts, name, entry.source.rig);
      return { assetId, root, dispose: () => root.dispose() };
    }

    const container = this.loader.get(assetId);
    if (container) {
      const instanced = entry.instancing === 'instances';
      if (instanced) this.loader.prepareInstancing(assetId);
      const root = new TransformNode(name, this.scene);
      const entries = container.instantiateModelsToScene((source) => `${name}:${source}`, false, {
        doNotInstantiate: !instanced,
        predicate: (entity: { name?: string }) => !AUTHORING_NODE.test(entity.name ?? ''),
      });
      for (const node of entries.rootNodes) node.parent = root;
      return {
        assetId,
        root,
        animationGroups: entries.animationGroups,
        dispose: () => {
          entries.dispose();
          root.dispose();
        },
      };
    }

    if (!this.warned.has(assetId)) {
      this.warned.add(assetId);
      console.warn(`[assets] ${assetId} not loaded – using greybox fallback (${entry.source.url})`);
      if (!this.loader.hasFailed(assetId)) void this.loader.load(assetId).catch(() => undefined);
    }
    const parts = entry.source.fallback ?? MISSING_PARTS;
    const root = this.greybox.instantiate(`${assetId}__FALLBACK`, parts, name, entry.source.fallbackRig);
    return { assetId, root, isFallback: true, dispose: () => root.dispose() };
  }
}
